// frontend/admin-portal/src/components/products/ProductImagesManager.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  ImageUploader,
  Gallery,
  Alert,
  Spinner,
  Button,
} from "@fiberhome/shared-ui";
import { fileService, resolveImageUrl } from "../../services/fileService";
import { productService } from "../../services/productService";

export default function ProductImagesManager({ product, onSaved }) {
  const [state, setState] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [recentlyUploaded, setRecentlyUploaded] = useState([]);
  const timers = useRef([]);

  useEffect(
    () => () => {
      timers.current.forEach((t) => clearTimeout(t));
    },
    [],
  );

  const parseImages = () => {
    let raw = product?.images;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        raw = [];
      }
    }
    if (!Array.isArray(raw)) return [];

    return raw
      .filter(Boolean)
      .map((img) => {
        if (typeof img === "string") {
          return {
            url: img,
            thumbnailUrl: img,
            originalName: "image",
            mimeType: "image/*",
          };
        }
        return {
          ...img,
          url: img.url || img.thumbnailUrl,
          thumbnailUrl: img.thumbnailUrl || img.url,
        };
      })
      .map((f) => ({
        ...f,
        url: resolveImageUrl(f.url),
        thumbnailUrl: resolveImageUrl(f.thumbnailUrl),
        // keep the raw URL — for delete-by-url
        rawUrl: typeof f.url === "string" ? f.url : f.rawUrl || f.url,
      }));
  };

  const normalized = parseImages();

  const saveImages = async (urls) => {
    await productService.update(product.id, { images: urls });
    onSaved?.();
  };

  const handleUpload = async (files) => {
    setState("uploading");
    setError("");
    setProgress(0);
    setRecentlyUploaded([]);

    try {
      const uploaded = await fileService.uploadImages(files, {
        entityType: "products",
        entityId: product.id,
        onProgress: setProgress,
      });

      if (uploaded[0]?.id) {
        fileService
          .attach(uploaded[0].id, {
            entityType: "products",
            entityId: product.id,
          })
          .catch(() => {});
      }

      const existing = parseImages().map((f) => f.rawUrl || f.url);
      const newUrls = uploaded.map((f) => f.url).filter(Boolean);
      const updated = [...existing, ...newUrls];

      await saveImages(updated);

      setRecentlyUploaded(newUrls);
      setState("done");

      timers.current.push(
        setTimeout(() => {
          setState("idle");
          setRecentlyUploaded([]);
        }, 1500),
      );
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Upload failed");
      setState("error");
    }
  };

  const handleRemove = async (_img, idx) => {
    const arr = parseImages();
    const removed = arr[idx];
    if (!removed) return;

    // 1. Remove from product.images
    const remaining = arr.map((f) => f.rawUrl || f.url);
    remaining.splice(idx, 1);
    await saveImages(remaining);

    // 2. Delete the physical file from file-service
    const targetUrl = removed.rawUrl || removed.url;
    if (targetUrl) {
      try {
        await fileService.removeByUrl(targetUrl);
      } catch (err) {
        // Non-fatal — log it, don't block the UI
        // eslint-disable-next-line no-console
        console.warn(
          "[ProductImagesManager] removeByUrl failed:",
          err?.response?.data || err.message,
        );
      }
    }
  };

  const handleMakePrimary = async (_img, idx) => {
    if (idx === 0) return;
    const arr = parseImages().map((f) => f.rawUrl || f.url);
    const [picked] = arr.splice(idx, 1);
    arr.unshift(picked);
    await saveImages(arr);
  };

  const handleReorder = async (fromIdx, toIdx) => {
    const arr = parseImages().map((f) => f.rawUrl || f.url);
    const [picked] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, picked);
    await saveImages(arr);
  };

  const renderUploader = () => {
    if (state === "uploading") {
      return (
        <div className="fh-upload-progress">
          <div className="fh-upload-progress__spinner">
            <Spinner size="md" />
          </div>
          <div className="fh-upload-progress__info">
            <div className="fh-upload-progress__title">
              Uploading {progress > 0 && progress < 100 ? `${progress}%` : "…"}
            </div>
            <div className="fh-upload-progress__sub">
              {progress > 0
                ? "Almost done…"
                : "Processing files — this should take a moment"}
            </div>
          </div>
          <div className="fh-upload-progress__bar">
            <div
              className="fh-upload-progress__fill"
              style={{ width: `${Math.max(progress, 15)}%` }}
            />
          </div>
        </div>
      );
    }

    if (state === "done") {
      return (
        <div className="fh-upload-done">
          <div className="fh-upload-done__icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            <div className="fh-upload-done__title">Upload complete</div>
            <div className="fh-upload-done__sub">
              {recentlyUploaded.length} image
              {recentlyUploaded.length === 1 ? "" : "s"} added
            </div>
          </div>
        </div>
      );
    }

    return (
      <ImageUploader
        onFiles={handleUpload}
        multiple
        maxFiles={10}
        maxSizeMB={10}
      />
    );
  };

  return (
    <div>
      {error && state === "error" && (
        <Alert variant="danger" className="mb-3">
          {error}
          <div className="mt-2">
            <Button size="sm" variant="ghost" onClick={() => setState("idle")}>
              Try again
            </Button>
          </div>
        </Alert>
      )}

      <Gallery
        images={normalized}
        onRemove={handleRemove}
        onMakePrimary={handleMakePrimary}
        onReorder={handleReorder}
        emptyMessage="No photos yet"
      />

      <div className="mt-3">{renderUploader()}</div>
    </div>
  );
}
